import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useConversationParticipants } from "@/hooks/useConversationParticipants";
import {
  COACHING_GET,
  CONVERSATION_DATA_TABLE_ROWS_GET,
  CONVERSATION_GET,
  CONVERSATION_SEGMENT_GET,
  DATA_EXTRACTION_STATUS_GET,
  SUGGESTED_PARTICIPANTS_GET,
  getCoachingByConversationId,
  getConversation,
  getConversationDataTableRows,
  getConversationSegment,
  getDataExtractionStatus,
  getSuggestedParticipants,
} from "@/api";
import { PARTICIPANTS_GET } from "@/api/client/participant";
import {
  AdvisorCoachingResponse,
  CoachingStatus,
  Conversation,
  ConversationPurposeEntity,
  ConversationStatus,
  DataExtractionStatus,
} from "@/types";
import {
  isPageReady as checkIsPageReady,
  isAnyStepProcessing,
  hasProcessingSteps,
} from "@/components/pages/Conversations/ConversationsDetails/utils/processingSteps";

interface Params {
  conversationId: string;
  hasNoAccess: boolean;
  isWebSocketConnected: boolean;
}

export const useConversationQueries = ({
  conversationId,
  hasNoAccess,
  isWebSocketConnected,
}: Params) => {
  const queryClient = useQueryClient();

  // The websocket-subscriptions hook is called *after* this hook in the
  // provider (because it depends on linkedClientIds, which comes from
  // conversation). To match the existing behavior where the conversation
  // query's refetchInterval reads the most up-to-date isWebSocketConnected
  // value, we mirror the param into a ref the refetchInterval closures can
  // read at refetch time instead of capturing the call-time value.
  const isWebSocketConnectedRef = useRef(isWebSocketConnected);
  useEffect(() => {
    isWebSocketConnectedRef.current = isWebSocketConnected;
  }, [isWebSocketConnected]);

  const {
    data: conversation,
    isSuccess,
    error: conversationError,
  } = useQuery({
    queryKey: [CONVERSATION_GET, conversationId],
    queryFn: () => getConversation(conversationId),
    enabled: !!conversationId,
    refetchInterval: (query) => {
      // Don't poll if user has no access
      if (hasNoAccess) return false;

      // Don't poll if WebSocket is connected - rely on real-time updates
      if (isWebSocketConnectedRef.current) return false;

      const conversationData = query.state.data as Conversation | undefined;

      // When processing steps exist, poll while any step is non-terminal
      if (hasProcessingSteps(conversationData)) {
        if (isAnyStepProcessing(conversationData)) return 10000;
        // All steps terminal — also wait for overall status DONE
        if (conversationData?.status === ConversationStatus.DONE) return false;
        return 10000;
      }

      // Legacy: don't poll if conversation is done
      if (conversationData?.status === ConversationStatus.DONE) return false;

      // Poll every 10 seconds for processing conversations (fallback when WebSocket is not connected)
      return 10000;
    },
    retry: 1,
  });

  // Coaching is disabled for self-dictation and all-purpose meetings.
  // Derived from the conversation's purpose so the coaching query stays
  // disabled until we know the purpose.
  const isCoachingDisabled =
    conversation?.purpose === ConversationPurposeEntity.SelfDictationMeeting ||
    conversation?.purpose === ConversationPurposeEntity.AllPurposeMeeting;

  // Data extraction runs for client and self-dictation meetings, so the Data
  // tab and its JSON export are enabled only for those purposes. Allowlist
  // rather than denylist: a missing or unknown purpose stays disabled.
  const isDataEnabled =
    conversation?.purpose === ConversationPurposeEntity.GeneralClientMeeting ||
    conversation?.purpose === ConversationPurposeEntity.IntroMeeting ||
    conversation?.purpose === ConversationPurposeEntity.SelfDictationMeeting;

  // Enable transcript/suggested participants when page is ready (processing steps terminal or legacy DONE)
  const pageReady = checkIsPageReady(conversation);

  const { data: transcriptMessages, isPending: isTranscriptMessagesPending } =
    useQuery({
      queryKey: [CONVERSATION_SEGMENT_GET, conversationId],
      queryFn: () => getConversationSegment(conversationId),
      enabled: pageReady,
    });

  const { data: suggestedParticipants } = useQuery({
    queryKey: [SUGGESTED_PARTICIPANTS_GET, conversationId],
    queryFn: () => getSuggestedParticipants(conversationId),
    enabled: !!conversationId && pageReady,
  });

  const { data: coachingData, isPending: isCoachingFetching } = useQuery({
    queryKey: [COACHING_GET, conversationId],
    queryFn: () => getCoachingByConversationId(conversationId),
    enabled: !!conversationId && !isCoachingDisabled,
    refetchInterval: (query) => {
      // Don't poll if WebSocket is connected - rely on real-time updates
      if (isWebSocketConnectedRef.current) return false;

      const data = query.state.data as AdvisorCoachingResponse | undefined;

      // Poll if status is PENDING or PROCESSING
      if (
        data?.status === CoachingStatus.PENDING ||
        data?.status === CoachingStatus.PROCESSING
      ) {
        return 10000;
      }

      // Poll if no data returned but not an error (old conversations case)
      if (!data && query.state.status !== "error") {
        return 10000;
      }

      // Stop polling when COMPLETED or ERROR
      return false;
    },
  });

  // Coaching is loading if we're fetching OR if status is PENDING/PROCESSING OR no data yet (still polling)
  const isCoachingLoading = (() => {
    if (isCoachingFetching) return true;
    if (!coachingData) return true; // No data yet means still loading/polling
    return (
      coachingData.status === CoachingStatus.PENDING ||
      coachingData.status === CoachingStatus.PROCESSING
    );
  })();

  // Show error state only if we have data and status is ERROR
  const hasCoachingError = (() => {
    if (isCoachingFetching) return false;
    if (!coachingData) return false; // No data = still loading, not an error
    return coachingData.status === CoachingStatus.ERROR;
  })();

  // Poll for data extraction status when feature is enabled
  const { data: dataExtractionData } = useQuery({
    queryKey: [DATA_EXTRACTION_STATUS_GET, conversationId],
    queryFn: () => getDataExtractionStatus(conversationId),
    enabled: !!conversationId && pageReady,
    refetchInterval: (query) => {
      // Don't poll if WebSocket is connected - rely on real-time updates
      if (isWebSocketConnectedRef.current) return false;

      const data = query.state.data;

      // Poll if status is PENDING or PROCESSING
      if (
        data?.status === DataExtractionStatus.PENDING ||
        data?.status === DataExtractionStatus.PROCESSING
      ) {
        return 5000; // Poll every 5 seconds
      }

      // Stop polling when COMPLETED or ERROR
      return false;
    },
  });

  // Separate query for data table rows when data extraction is queued
  // Enabled as soon as page is ready so it's active when we invalidate
  const { data: dataTableRows } = useQuery({
    queryKey: [CONVERSATION_DATA_TABLE_ROWS_GET, conversationId],
    queryFn: () => getConversationDataTableRows(conversationId),
    enabled: !!conversationId && pageReady,
  });

  const { data: participants, isPending: isParticipantsPending } =
    useConversationParticipants(conversationId);

  // The agent service creates chat sessions inline on the first message, so
  // there is no pre-initialization step. Messages are sent directly via the
  // agent service instead of being queued, so chat is never "initializing".
  const isChatInitializing = false;

  // Refetch data table rows when extraction completes
  // This runs after React re-renders with the new status, ensuring the query is active
  useEffect(() => {
    if (dataExtractionData?.status === DataExtractionStatus.COMPLETED) {
      queryClient.invalidateQueries({
        queryKey: [CONVERSATION_DATA_TABLE_ROWS_GET, conversationId],
      });
    }
    queryClient.invalidateQueries({
      queryKey: [PARTICIPANTS_GET, conversationId],
    });
  }, [dataExtractionData?.status, conversationId, queryClient]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [PARTICIPANTS_GET, conversationId],
    });
  }, [conversation?.status, conversationId, queryClient]);

  return {
    conversation,
    isSuccess,
    conversationError,
    transcriptMessages,
    isTranscriptMessagesPending,
    suggestedParticipants,
    coachingData,
    isCoachingLoading,
    hasCoachingError,
    isCoachingDisabled,
    isDataEnabled,
    dataExtractionData,
    dataTableRows,
    participants,
    isParticipantsPending,
    isChatInitializing,
  };
};
